import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Award, ArrowRight, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Certificate() {
  const { user } = useAuth();
  const [cert, setCert] = useState(null);
  const [progress, setProgress] = useState({ attempts: [] });
  const [courses, setCourses] = useState([]);

  const paid = !!user && user.plan && user.plan !== "free";

  useEffect(() => {
    (async () => {
      const [c, p, cs] = await Promise.all([
        api.get("/certificates/me"),
        api.get("/progress"),
        api.get("/courses"),
      ]);
      setCert(c.data.certificate);
      setProgress(p.data);
      setCourses(cs.data);
    })();
  }, []);

  const passedIds = new Set(progress.attempts.filter((a) => a.passed).map((a) => a.course_id));
  const total = courses.length || 8;
  const passed = passedIds.size;

  const downloadPdf = () => {
    if (!cert) return;
    window.open(`${API}/certificates/${cert.certificate_id}/pdf`, "_blank");
  };

  if (!paid && !cert) {
    return (
      <div className="p-6 lg:p-10 max-w-5xl" data-testid="certificate-locked-page">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Credential</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950 mt-1">Your certificate</h1>

        <div className="mt-8 border border-slate-200 rounded-sm p-8 bg-slate-50 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-950 grid place-items-center rounded-sm text-white">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Paid plan required</div>
                <div className="font-display font-bold text-xl text-slate-950">Unlock the Platinum 1 PDF certificate</div>
              </div>
            </div>
            <p className="text-slate-700 mt-4">
              The downloadable Platinum 1 Certified credential is included in every paid plan. Upgrade once,
              pass all 8 module quizzes, and your PDF is yours to share with clients, GCs, and employers.
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-800">
              {[
                "Personalized with your full name",
                "Unique credential ID",
                "Print-ready landscape A4 PDF",
                "Listed in our verified directory (Elite)",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-600" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4">
            <Link to="/pricing" data-testid="cert-locked-upgrade-btn" className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" /> See plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="certificate-page">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Credential</div>
      <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950 mt-1">Your certificate</h1>

      {cert ? (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <div className="border-4 border-orange-600 p-1 rounded-sm">
              <div className="border border-slate-300 p-10 bg-white relative overflow-hidden">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-[0.3em] font-bold text-orange-600">Platinum 1 Certified · Handyman Program</div>
                  <div className="font-display text-4xl font-black tracking-tighter text-slate-950 mt-6">
                    Certificate of Certification
                  </div>
                  <div className="text-slate-500 mt-3">This certifies that</div>
                  <div className="font-display text-3xl font-bold text-slate-950 mt-4">
                    {cert.user_name || cert.user_email}
                  </div>
                  <div className="mx-auto w-48 h-px bg-orange-600 mt-3" />
                  <p className="text-slate-600 mt-5 leading-relaxed max-w-xl mx-auto text-sm">
                    has successfully completed the Platinum 1 Handyman Certification curriculum, demonstrating
                    proficiency in Safety, Tools, Plumbing, Electrical, Carpentry, Drywall & Painting, Tiling,
                    and Customer Service.
                  </p>
                  <div className="mt-8 flex items-center justify-center gap-12 text-xs uppercase tracking-[0.2em] font-bold">
                    <div className="text-left">
                      <div className="text-slate-500">Credential ID</div>
                      <div className="text-slate-900 font-mono">{cert.certificate_id}</div>
                    </div>
                    <div className="w-16 h-16 bg-orange-600 grid place-items-center rounded-full text-white">
                      <Award className="w-8 h-8" />
                    </div>
                    <div className="text-left">
                      <div className="text-slate-500">Issued</div>
                      <div className="text-slate-900">{cert.issued_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="border border-slate-200 rounded-sm p-6 bg-slate-50">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Award</div>
              <div className="font-display text-2xl font-bold text-slate-950 mt-1">You did it.</div>
              <p className="text-slate-600 mt-3 text-sm">
                Your Platinum 1 Certified credential is now active. Download the PDF for printing or share with
                clients & employers.
              </p>
              <button
                onClick={downloadPdf}
                data-testid="download-certificate-btn"
                className="mt-5 w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 border border-slate-200 rounded-sm p-8 bg-slate-50">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-slate-500" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Not yet earned</div>
          </div>
          <div className="font-display text-2xl font-bold text-slate-950 mt-3">
            {passed} of {total} modules passed
          </div>
          <p className="text-slate-600 mt-2 max-w-xl">
            Pass the certification quiz in every module with a score of 75% or higher to unlock your Platinum 1
            Certified credential.
          </p>
          <Link
            to="/courses"
            data-testid="goto-courses-from-cert-btn"
            className="mt-5 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold"
          >
            Continue learning <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
